// Hilos.js
// Red social tipo "hilos" (Threads/X): publicaciones de texto corto con
// fotos/videos, me gusta, republicar, comentar y chatear con el autor.
// Accesible en toda la versión web (el botón central del bottom nav solo la
// muestra en desktop, ver BottomToolbar.js; en mobile web se llega por el
// menú del ícono de mapa en CustomToolbar.js). En la app nativa el botón
// central sigue yendo a QRScanner directo, sin este menú.
import React, { useEffect, useMemo, useRef, useState } from "react";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Popover,
  Paper,
  Dialog,
  DialogContent,
  List,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
  startAt,
  endAt,
  getDocs,
  serverTimestamp,
  deleteField,
  arrayUnion,
  arrayRemove,
  increment,
} from "firebase/firestore";
import { EmojiEmotions as EmojiEmotionsIcon } from "../icons";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../icons";
import { PhotoCamera as PhotoCameraIcon } from "../icons";
import { VideoLibrary as VideoLibraryIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import { Favorite as FavoriteIcon } from "../icons";
import { FavoriteBorder as FavoriteBorderIcon } from "../icons";
import { AddReactionOutlined as AddReactionOutlinedIcon } from "../icons";
import { ChatBubbleOutline as ChatBubbleOutlineIcon } from "../icons";
import { Repeat as RepeatIcon } from "../icons";
import { Send as SendIcon } from "../icons";
import { Forum as ForumIcon } from "../icons";
import { MoreVert as MoreVertIcon } from "../icons";
import { Tag as TagIcon } from "../icons";
import { AlternateEmail as AlternateEmailIcon } from "../icons";
import { auth, db, uploadImage, uploadVideo } from "../firebase";
import { T } from "./Translate";
import BackButton from "./BackButton";
import VerifiedBadge from "./VerifiedBadge";
import useAuthorPhotos from "./useAuthorPhotos";
import BottomToolbar from "./BottomToolbar";
import ScrollAwareVideo from "../components/ScrollAwareVideo";
import CameraCapture from "../components/CameraCapture";
import EditThreadDialog from "../components/EditThreadDialog";
import { isMobileDevice } from "../utils/device";
import ThreadLinkCards, { searchLinkablePublications, LinkSuggestionRow } from "../components/ThreadLinkCards";
import { formatDate, reportItem } from "./homeUtils";
import { blockUser, unblockUser, fetchBlockedIds } from "../utils/blockedUsers";
import { barColors, brandColors } from "../theme";
import { pageContentSx } from "../pageLayout";
import {
  mediaPreviewStyle,
  mediaPreviewBoxSx,
  removeMediaButtonSx,
  removeMediaIconSx,
} from "./mediaFormStyles";

const REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "😡"];

const EMOJIS = [
  "😀", "😂", "😍", "😎", "🥳", "😢", "😡", "👍", "👎", "🙏",
  "🎉", "🔥", "💯", "❤️", "💙", "💚", "💛", "🧡", "💜", "🤍",
  "✨", "🚀", "🌟", "😅", "🤔", "😴", "🤯", "🥹", "😇", "🤗",
  "🙌", "👏", "💪", "🤝", "👀", "💬", "📸", "🎥", "🎁", "⭐",
  "✅", "❌", "⚡", "🌈", "☀️", "🌙", "⚽", "🍀", "😘", "🥰",
];

const buildOriginalSnapshot = (thread) => (
  thread.repostOf
    ? {
        uid: thread.originalUid,
        name: thread.originalName,
        image: thread.originalImage,
        text: thread.originalText,
        images: thread.originalImages || [],
        videos: thread.originalVideos || [],
        mentions: thread.originalMentions || [],
        links: thread.originalLinks || [],
      }
    : {
        uid: thread.uid,
        name: thread.name,
        image: thread.image,
        text: thread.text,
        images: thread.images || [],
        videos: thread.videos || [],
        mentions: thread.mentions || [],
        links: thread.links || [],
      }
);

// Reconoce "@usuario"/"#tema", URLs, emails y teléfonos dentro de un texto
// ya publicado, para resaltarlos y hacerlos clickeables al renderizar (ver
// renderRichText). El orden de las alternativas importa: URL/email antes
// que mención, así "test@dominio.com" no se interpreta como "@dominio" con
// "test" suelto adelante (la alternativa de email gana porque arranca a
// matchear desde la "t", antes de que el escaneo llegue al "@").
// El teléfono pide al menos un separador (espacio/guion/paréntesis/"+")
// para no confundir cualquier número largo suelto con un teléfono: no hay
// forma de validar un teléfono real sin una librería tipo libphonenumber,
// así que esto es una heurística, no una validación.
const RICH_TEXT_RE = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+)|([\w.+-]+@[\w-]+\.[a-zA-Z]{2,})|([@#][\p{L}\p{N}_]+)|(\+?\d[\d\s().-]{5,14}\d)/gu;

// Autocompletado del compositor: busca por prefijo de displayName, igual
// que searchUsersByName en eventsUtils.js (mismo patrón de query), con el
// límite superior del rango (, el punto de código más alto de la BMP)
// para que matchee cualquier nombre que empiece con el prefijo escrito, no
// solo el nombre exacto.
const PREFIX_UPPER_BOUND = "";

// followingIds: Set de uids que sigue el usuario actual (userFollowing/{uid}
// en Firestore). Solo se sugieren cuentas de esa lista (pedido explícito),
// así que se pide un límite más alto que los 6 finales para tener margen
// tras filtrar por seguidos.
const searchMentionCandidates = async (term, followingIds) => {
  const cleanTerm = term.trim();
  if (!cleanTerm || !followingIds || followingIds.size === 0) return [];
  try {
    const usersQuery = query(
      collection(db, "users"),
      orderBy("displayName"),
      startAt(cleanTerm),
      endAt(cleanTerm + PREFIX_UPPER_BOUND),
      limit(25)
    );
    const snapshot = await getDocs(usersQuery);
    const myUid = auth.currentUser?.uid;
    return snapshot.docs
      .map((docSnap) => ({ uid: docSnap.id, ...docSnap.data() }))
      .filter((user) => user.uid !== myUid && followingIds.has(user.uid))
      .slice(0, 6)
      .map((user) => ({
        uid: user.uid,
        name: user.displayName || user.name || "Usuario",
        image: user.profileImageUrl || "",
        verified: !!user.verified,
      }));
  } catch (error) {
    console.error("Error al buscar usuarios para mención:", error);
    return [];
  }
};

const Hilos = () => {
  const navigate = useNavigate();
  // Cámara: siempre en celulares/tablets (también la app), nunca en computadora.
  const showCameraButtons = isMobileDevice();
  const [myProfile, setMyProfile] = useState(null);

  const [composeText, setComposeText] = useState("");
  const [composeImages, setComposeImages] = useState([]);
  const [composeVideos, setComposeVideos] = useState([]);
  const [cameraMode, setCameraMode] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [reactionPickerFor, setReactionPickerFor] = useState(null);

  // Autocompletado de "@usuario" en el compositor: composeMentions guarda
  // los {uid, name} elegidos del dropdown (para linkear el mention al
  // publicar); mentionQuery/mentionRange trackean el "@parcial" que se está
  // escribiendo y en qué posición del texto reemplazarlo al elegir alguien.
  // Solo sugiere cuentas que el usuario sigue (followingIds), a diferencia
  // del autocompletado de hashtags que no tiene esa restricción.
  const composeInputRef = useRef(null);
  const mentionBlurTimeout = useRef(null);
  const [composeMentions, setComposeMentions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionRange, setMentionRange] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [followingIds, setFollowingIds] = useState(new Set());

  // Autocompletado de "#hashtag": a diferencia de las menciones, no pega a
  // Firestore — sugiere entre los hashtags ya usados en los hilos cargados
  // (mismo alcance que el filtro por hashtag del feed, ver activeHashtag).
  const [hashtagQuery, setHashtagQuery] = useState(null);
  const [hashtagRange, setHashtagRange] = useState(null);

  // Con "#algo" también se sugieren publicaciones (eventos, ofertas, autos,
  // propiedades y tiendas) cuyo título contiene "algo"; al elegir una, el
  // título queda en el texto y la publicación se guarda en `links` del hilo
  // para mostrarla como tarjeta (ver ThreadLinkCards).
  const [composeLinks, setComposeLinks] = useState([]);
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);

  // Índice resaltado del popup abierto (mención o hashtag, nunca los dos a
  // la vez), para navegar con flechas ↑/↓ y confirmar con Enter/Tab.
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Filtro por hashtag: clickear un "#tema" en un hilo publicado filtra el
  // feed ya cargado (no hay índice server-side de hashtags, mismo alcance
  // que el resto de esta pantalla que solo pagina los últimos 50 hilos).
  const [activeHashtag, setActiveHashtag] = useState(null);

  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);

  const [commentsFor, setCommentsFor] = useState(null);
  const [comments, setComments] = useState([]);
  const authorPhotos = useAuthorPhotos([
    ...threads.map((thread) => (thread.repostOf ? thread.originalUid : thread.uid)),
    ...comments.map((c) => c.uid),
  ]);
  const [commentText, setCommentText] = useState("");

  const [previewImage, setPreviewImage] = useState(null);

  const [blockedIds, setBlockedIds] = useState([]);
  const [optionsMenu, setOptionsMenu] = useState(null);
  const [editingThread, setEditingThread] = useState(null);

  // El AppBar y el compositor quedan position:fixed, uno debajo del otro.
  // Ambos altos son dinámicos (el AppBar por safe-area/zoom del dispositivo,
  // el compositor por texto multilínea o previews de imagen/video), así que
  // se miden con ResizeObserver en vez de asumir un valor fijo: un alto
  // asumido incorrecto deja una franja sin cubrir entre ambos por donde se
  // ve el feed al scrollear.
  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(0);
  const composeRef = useRef(null);
  const [composeHeight, setComposeHeight] = useState(0);

  useEffect(() => {
    const el = appBarRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setAppBarHeight(entries[0].contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = composeRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setComposeHeight(entries[0].contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Busca candidatos a mención con debounce mientras el usuario escribe
  // "@algo" en el compositor (ver handleComposeTextChange). mentionLoading
  // deja mostrar el popup con un spinner en vez de no mostrar nada durante
  // los ~250ms de debounce + el viaje a Firestore.
  useEffect(() => {
    if (mentionQuery === null) {
      setMentionResults([]);
      setMentionLoading(false);
      return;
    }
    let active = true;
    setMentionLoading(true);
    const timer = setTimeout(async () => {
      const results = await searchMentionCandidates(mentionQuery, followingIds);
      if (active) {
        setMentionResults(results);
        setMentionLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [mentionQuery, followingIds]);

  useEffect(() => {
    if (!hashtagQuery || hashtagQuery.length < 2) {
      setLinkResults([]);
      setLinkLoading(false);
      return;
    }
    let active = true;
    setLinkLoading(true);
    const timer = setTimeout(async () => {
      const results = await searchLinkablePublications(hashtagQuery);
      if (active) {
        setLinkResults(results);
        setLinkLoading(false);
      }
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [hashtagQuery]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    fetchBlockedIds(user.uid).then(setBlockedIds);
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    getDoc(doc(db, "userFollowing", user.uid)).then((snap) => {
      setFollowingIds(new Set(Object.keys(snap.data() || {})));
    });
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    getDoc(doc(db, "users", user.uid)).then((snap) => {
      const data = snap.data() || {};
      setMyProfile({
        name: data.displayName || data.name || "Usuario",
        image: data.profileImageUrl || "",
      });
    });
  }, []);

  // Trae los hilos de a 20 desde Firestore: al acercarse al último visible se
  // amplía el limit de la query y onSnapshot suma los siguientes 20.
  const { limit: threadsLimit, sentinelRef: threadsSentinelRef } = useInfiniteScroll();

  useEffect(() => {
    const q = query(collection(db, "threads"), orderBy("createdAt", "desc"), limit(threadsLimit));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setThreads(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar hilos:", error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [threadsLimit]);

  useEffect(() => {
    if (!commentsFor) {
      setComments([]);
      return;
    }
    const q = query(
      collection(db, "threadComments"),
      where("threadId", "==", commentsFor),
      orderBy("createdAt", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setComments(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [commentsFor]);

  // IDs de hilos originales que ya reposteé, para poder togglear el botón.
  const myRepostedOriginalIds = useMemo(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return new Set();
    return new Set(
      threads.filter((t) => t.repostOf && t.uid === uid).map((t) => t.repostOf)
    );
  }, [threads]);

  // Hashtags ya usados entre los hilos cargados, para sugerir en el
  // compositor (a diferencia de las menciones, no hay query a Firestore:
  // se arma con lo que ya está en memoria, mismo alcance que activeHashtag).
  const availableHashtags = useMemo(() => {
    const set = new Set();
    threads.forEach((t) => {
      const matches = (t.text || "").match(/#[\p{L}\p{N}_]+/gu) || [];
      matches.forEach((m) => set.add(m.slice(1)));
    });
    return Array.from(set);
  }, [threads]);

  const hashtagResults = useMemo(() => {
    if (!hashtagQuery || hashtagQuery.length < 2) return [];
    const term = hashtagQuery.toLowerCase();
    return availableHashtags
      .filter((tag) => tag.toLowerCase().startsWith(term))
      .slice(0, 6);
  }, [hashtagQuery, availableHashtags]);

  // Popup de "#": primero las publicaciones, después los hashtags usados.
  const hashtagSuggestions = useMemo(() => [
    ...linkResults.map((link) => ({ kind: "link", link })),
    ...hashtagResults.slice(0, 3).map((tag) => ({ kind: "tag", tag })),
  ], [linkResults, hashtagResults]);

  // Vuelve a arrancar la navegación por teclado del popup desde el primer
  // resultado cada vez que cambian las sugerencias mostradas.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [mentionResults, hashtagSuggestions]);

  // Detecta un "@parcial" o "#parcial" justo antes del cursor (ej:
  // "hola @jul" / "hola #ju") para abrir el dropdown correspondiente;
  // requiere inicio de texto o un espacio antes del "@"/"#" para no
  // disparar dentro de un email o similar. Las menciones sugieren desde la
  // 1ra letra, los hashtags desde la 2da (pedido explícito).
  const handleComposeTextChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setComposeText(value);
    const beforeCursor = value.slice(0, cursor);
    const mentionMatch = beforeCursor.match(/(?:^|\s)@([^\s@#]{1,30})$/);
    const hashtagMatch = beforeCursor.match(/(?:^|\s)#([^\s@#]{2,30})$/);
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setMentionRange({ start: cursor - mentionMatch[1].length, end: cursor });
      setHashtagQuery(null);
      setHashtagRange(null);
    } else if (hashtagMatch) {
      setHashtagQuery(hashtagMatch[1]);
      setHashtagRange({ start: cursor - hashtagMatch[1].length, end: cursor });
      setMentionQuery(null);
      setMentionRange(null);
    } else {
      setMentionQuery(null);
      setMentionRange(null);
      setHashtagQuery(null);
      setHashtagRange(null);
    }
  };

  const handleSelectMention = (user) => {
    if (!mentionRange) return;
    const before = composeText.slice(0, mentionRange.start);
    const after = composeText.slice(mentionRange.end);
    const insertion = `${user.name} `;
    const newText = `${before}${insertion}${after}`;
    setComposeText(newText);
    setComposeMentions((prev) => (
      prev.some((m) => m.uid === user.uid) ? prev : [...prev, { uid: user.uid, name: user.name }]
    ));
    setMentionQuery(null);
    setMentionResults([]);
    setMentionRange(null);
    requestAnimationFrame(() => {
      const el = composeInputRef.current;
      if (!el) return;
      const pos = before.length + insertion.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSelectHashtag = (suggestion) => {
    if (!hashtagRange) return;
    const isLink = suggestion.kind === "link";
    // Una publicación reemplaza también el "#": queda su título como texto.
    const before = composeText.slice(0, isLink ? hashtagRange.start - 1 : hashtagRange.start);
    const after = composeText.slice(hashtagRange.end);
    const insertion = `${isLink ? suggestion.link.title : suggestion.tag} `;
    if (isLink) {
      const { type, id, title, image } = suggestion.link;
      setComposeLinks((prev) => (
        prev.some((l) => l.type === type && l.id === id) ? prev : [...prev, { type, id, title, image }]
      ));
    }
    const newText = `${before}${insertion}${after}`;
    setComposeText(newText);
    setHashtagQuery(null);
    setHashtagRange(null);
    requestAnimationFrame(() => {
      const el = composeInputRef.current;
      if (!el) return;
      const pos = before.length + insertion.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const handleUploadImages = async (event) => {
    // Copia antes de limpiar el input: en Chrome/WebView el FileList es el
    // mismo objeto y queda vacío al hacer value = null
    const files = Array.from(event.target.files || []);
    event.target.value = null;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        urls.push(await uploadImage(file));
      }
      setComposeImages((prev) => [...prev, ...urls]);
    } catch (error) {
      console.error("Error al subir imagen:", error);
      alert(`No se pudo subir la imagen: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = async (file) => {
    const mode = cameraMode;
    setCameraMode(null);
    setUploading(true);
    try {
      if (mode === "video") {
        const url = await uploadVideo(file);
        setComposeVideos((prev) => [...prev, url]);
      } else {
        const url = await uploadImage(file);
        setComposeImages((prev) => [...prev, url]);
      }
    } catch (error) {
      console.error("Error al subir captura de cámara:", error);
      alert(`No se pudo subir la captura: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handlePost = async () => {
    const user = auth.currentUser;
    if (!user) return;
    if (!composeText.trim() && composeImages.length === 0 && composeVideos.length === 0) return;

    setPosting(true);
    try {
      const finalText = composeText.trim();
      // Solo se guardan las menciones elegidas del dropdown que sigan
      // presentes en el texto final (si el usuario borró un "@fulano" ya
      // elegido, no queremos linkear un mention fantasma al publicar).
      const usedMentions = composeMentions.filter((m) => finalText.includes(`@${m.name}`));
      const usedLinks = composeLinks.filter((l) => finalText.includes(l.title));
      await addDoc(collection(db, "threads"), {
        uid: user.uid,
        name: myProfile?.name || "Usuario",
        image: myProfile?.image || "",
        text: finalText,
        images: composeImages,
        videos: composeVideos,
        mentions: usedMentions,
        links: usedLinks,
        likes: [],
        reactions: {},
        commentsCount: 0,
        repostsCount: 0,
        repostOf: null,
        createdAt: serverTimestamp(),
      });
      setComposeText("");
      setComposeImages([]);
      setComposeVideos([]);
      setComposeMentions([]);
      setComposeLinks([]);
    } catch (error) {
      console.error("Error al publicar el hilo:", error);
    } finally {
      setPosting(false);
    }
  };

  const handleToggleLike = async (thread) => {
    const user = auth.currentUser;
    if (!user) return;
    const liked = (thread.likes || []).includes(user.uid);
    try {
      await updateDoc(doc(db, "threads", thread.id), {
        likes: liked ? arrayRemove(user.uid) : arrayUnion(user.uid),
      });
    } catch (error) {
      console.error("Error al dar me gusta:", error);
    }
  };

  // Reaccionar (aparte del "me gusta"): click en el mismo emoji que ya
  // tenías puesto lo saca, click en otro lo cambia. Se guarda un mapa
  // {uid: emoji} para poder tener varios tipos de reacción por publicación.
  const handleSetReaction = async (thread, emoji) => {
    const user = auth.currentUser;
    if (!user) return;
    const current = thread.reactions?.[user.uid];
    try {
      await updateDoc(doc(db, "threads", thread.id), {
        [`reactions.${user.uid}`]: current === emoji ? deleteField() : emoji,
      });
    } catch (error) {
      console.error("Error al reaccionar:", error);
    } finally {
      setReactionPickerFor(null);
    }
  };

  const handleToggleRepost = async (thread) => {
    const user = auth.currentUser;
    if (!user) return;
    const originalId = thread.repostOf || thread.id;
    const alreadyReposted = myRepostedOriginalIds.has(originalId);

    try {
      if (alreadyReposted) {
        const mine = threads.find((t) => t.repostOf === originalId && t.uid === user.uid);
        if (mine) {
          await deleteDoc(doc(db, "threads", mine.id));
          await updateDoc(doc(db, "threads", originalId), { repostsCount: increment(-1) });
        }
        return;
      }

      const original = buildOriginalSnapshot(thread);
      await addDoc(collection(db, "threads"), {
        uid: user.uid,
        name: myProfile?.name || "Usuario",
        image: myProfile?.image || "",
        text: "",
        images: [],
        videos: [],
        likes: [],
        reactions: {},
        commentsCount: 0,
        repostsCount: 0,
        repostOf: originalId,
        originalUid: original.uid,
        originalName: original.name,
        originalImage: original.image,
        originalText: original.text,
        originalImages: original.images,
        originalVideos: original.videos,
        originalMentions: original.mentions,
        originalLinks: original.links,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "threads", originalId), { repostsCount: increment(1) });
    } catch (error) {
      console.error("Error al republicar:", error);
    }
  };

  const handlePostComment = async () => {
    const user = auth.currentUser;
    if (!user || !commentsFor || !commentText.trim()) return;
    try {
      await addDoc(collection(db, "threadComments"), {
        threadId: commentsFor,
        uid: user.uid,
        name: myProfile?.name || "Usuario",
        image: myProfile?.image || "",
        text: commentText.trim(),
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "threads", commentsFor), { commentsCount: increment(1) });
      setCommentText("");
    } catch (error) {
      console.error("Error al comentar:", error);
    }
  };

  const handleStartChat = async (targetUid) => {
    const user = auth.currentUser;
    if (!user || !targetUid || targetUid === user.uid) return;
    try {
      const sorted = [user.uid, targetUid].sort();
      const conversationId = `directo_${sorted[0]}_${sorted[1]}`;
      const convRef = doc(db, "conversations", conversationId);
      const snap = await getDoc(convRef);
      if (!snap.exists()) {
        await setDoc(convRef, {
          type: "directo",
          participants: sorted,
          createdAt: serverTimestamp(),
          lastMessageTime: serverTimestamp(),
          unreadCount: { [sorted[0]]: 0, [sorted[1]]: 0 },
        });
      }
      navigate(`/chatbox/directo/${targetUid}`);
    } catch (error) {
      console.error("Error al iniciar el chat:", error);
    }
  };

  const handleOpenOptionsMenu = (event, thread, author) => {
    setOptionsMenu({ anchorEl: event.currentTarget, thread, author });
  };

  const handleCloseOptionsMenu = () => setOptionsMenu(null);

  const handleDeleteThread = async (thread) => {
    try {
      await deleteDoc(doc(db, "threads", thread.id));
      if (thread.repostOf) {
        await updateDoc(doc(db, "threads", thread.repostOf), { repostsCount: increment(-1) });
      }
    } catch (error) {
      console.error("Error al eliminar el hilo:", error);
    } finally {
      handleCloseOptionsMenu();
    }
  };

  const handleReportThread = async (thread, author) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await reportItem({
        reporterId: user.uid,
        targetType: "thread",
        targetId: thread.id,
        companyId: author.uid,
      });
    } catch (error) {
      console.error("Error al reportar el hilo:", error);
    } finally {
      handleCloseOptionsMenu();
    }
  };

  const handleToggleBlockAuthor = async (author) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      if (blockedIds.includes(author.uid)) {
        await unblockUser(user.uid, author.uid);
        setBlockedIds((prev) => prev.filter((id) => id !== author.uid));
      } else {
        await blockUser(user.uid, author.uid);
        setBlockedIds((prev) => [...prev, author.uid]);
      }
    } catch (error) {
      console.error("Error al bloquear/desbloquear:", error);
    } finally {
      handleCloseOptionsMenu();
    }
  };

  const myUid = auth.currentUser?.uid;

  // Oculta hilos de gente bloqueada (bloqueado como reposteador o como autor
  // original de lo reposteado), mismo criterio que el resto del feed (offers).
  // Si hay un hashtag activo (click en un "#tema" del feed), además filtra
  // por ese texto entre los hilos ya cargados.
  const visibleThreads = threads.filter((thread) => {
    if (blockedIds.includes(thread.uid)) return false;
    if (thread.repostOf && blockedIds.includes(thread.originalUid)) return false;
    if (activeHashtag) {
      const text = (thread.repostOf ? thread.originalText : thread.text) || "";
      if (!text.toLowerCase().includes(`#${activeHashtag}`)) return false;
    }
    return true;
  });

  // Resalta "@mención"/"#hashtag"/URL/email/teléfono dentro del texto de un
  // hilo: la mención linkea al perfil si su uid está en el array `mentions`
  // guardado al publicar (ver handlePost), el hashtag filtra el feed al
  // clickearlo, y URL/email/teléfono quedan subrayados como link real
  // (<a href>) — un click ahí navega, y en la app nativa eso ya lo
  // intercepta shouldOverrideUrlLoading en MainActivity.kt: notiline.com
  // sigue en el WebView, cualquier otro sitio abre en un Custom Tab (el
  // "webview" del wrapper), tel:/mailto: abren la app de teléfono/mail.
  const linkSx = { color: brandColors.accent, textDecoration: "underline", wordBreak: "break-word" };

  const renderRichText = (text, mentions = []) => {
    if (!text) return null;
    const nodes = [];
    let lastIndex = 0;
    let key = 0;
    for (const match of text.matchAll(RICH_TEXT_RE)) {
      const [full, url, email, mentionOrHashtag, phone] = match;
      const start = match.index;
      if (start > lastIndex) nodes.push(text.slice(lastIndex, start));

      if (url) {
        // Puntuación de cierre de frase pegada al final ("mirá https://x.com."
        // -> el "." queda afuera del link, como texto normal después).
        const trimmedUrl = url.replace(/[.,;:!?)\]}'"]+$/, "");
        const trailingPunctuation = url.slice(trimmedUrl.length);
        const href = trimmedUrl.startsWith("http") ? trimmedUrl : `https://${trimmedUrl}`;
        nodes.push(
          <Box key={key++} component="a" href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
            {trimmedUrl}
          </Box>
        );
        if (trailingPunctuation) nodes.push(trailingPunctuation);
      } else if (email) {
        nodes.push(
          <Box key={key++} component="a" href={`mailto:${email}`} sx={linkSx}>
            {email}
          </Box>
        );
      } else if (phone) {
        nodes.push(
          <Box key={key++} component="a" href={`tel:${phone.replace(/[^\d+]/g, "")}`} sx={linkSx}>
            {phone}
          </Box>
        );
      } else if (mentionOrHashtag.startsWith("@")) {
        const mention = mentions.find((m) => m.name === mentionOrHashtag.slice(1));
        nodes.push(
          mention ? (
            <Box
              key={key++}
              component={Link}
              to={`/profile/${mention.uid}`}
              sx={{ color: brandColors.accent, fontWeight: 600, textDecoration: "none" }}
            >
              {mentionOrHashtag}
            </Box>
          ) : (
            <Box key={key++} component="span" sx={{ color: brandColors.accent, fontWeight: 600 }}>
              {mentionOrHashtag}
            </Box>
          )
        );
      } else {
        nodes.push(
          <Box
            key={key++}
            component="span"
            onClick={() => setActiveHashtag(mentionOrHashtag.slice(1).toLowerCase())}
            sx={{ color: brandColors.accent, fontWeight: 600, cursor: "pointer" }}
          >
            {mentionOrHashtag}
          </Box>
        );
      }

      lastIndex = start + full.length;
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    return nodes;
  };

  const renderThreadContent = (author, content, thread) => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
        <Avatar
          component={Link}
          to={`/profile/${author.uid}`}
          src={authorPhotos[author.uid] || author.image}
          sx={{ width: 40, height: 40 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component={Link}
            to={`/profile/${author.uid}`}
            sx={{ fontWeight: 600, color: brandColors.textPrimary, textDecoration: "none" }}
            noWrap
          >
            {author.name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={(e) => handleOpenOptionsMenu(e, thread, author)}>
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {content.text && (
        <Typography sx={{ whiteSpace: "pre-wrap", mb: content.images?.length || content.videos?.length ? 1.5 : 0 }}>
          {renderRichText(content.text, content.mentions)}
        </Typography>
      )}

      <ThreadLinkCards links={content.links} />

      {content.images?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {content.images.map((url, idx) => (
            <Box
              key={idx}
              component="img"
              src={url}
              alt=""
              onClick={() => setPreviewImage(url)}
              sx={{
                width: content.images.length === 1 ? "100%" : "calc(50% - 4px)",
                maxHeight: 320,
                objectFit: "cover",
                borderRadius: "10px",
                cursor: "pointer",
              }}
            />
          ))}
        </Box>
      )}

      {content.videos?.length > 0 && content.videos.map((url, idx) => (
        <Box key={idx} sx={{ borderRadius: "10px", overflow: "hidden", mb: 1, backgroundColor: "#000" }}>
          <ScrollAwareVideo
            src={url}
            controls
            playsInline
            muted
            style={{ width: "100%", maxHeight: 420, display: "block" }}
          />
        </Box>
      ))}
    </>
  );

  // Popup de sugerencias (@mención / #hashtag) del compositor: flotante,
  // angosto (no estira todo el ancho del textfield), con una "colita" que
  // lo conecta visualmente al textfield, header + estado de carga/vacío y
  // navegación por teclado (ver onKeyDown del TextField más abajo), para
  // que se sienta un dropdown completo y no una ventana suelta.
  const suggestionPopupSx = {
    position: "absolute",
    top: "calc(100% + 10px)",
    left: 12,
    minWidth: 240,
    maxWidth: 300,
    zIndex: 5,
    borderRadius: "16px",
    border: `1px solid ${brandColors.divider}`,
    boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
    backgroundColor: brandColors.backgroundPaper,
    backgroundImage: "none",
    overflow: "hidden",
    animation: "hilosSuggestPopIn 140ms ease-out",
    "@keyframes hilosSuggestPopIn": {
      from: { opacity: 0, transform: "translateY(-6px) scale(0.96)" },
      to: { opacity: 1, transform: "translateY(0) scale(1)" },
    },
  };

  // "Colita" que apunta hacia el textfield, para que el popup no se sienta
  // desconectado de lo que se está escribiendo.
  const suggestionNubSx = {
    position: "absolute",
    top: -5,
    left: 16,
    width: 10,
    height: 10,
    backgroundColor: brandColors.backgroundPaper,
    borderLeft: `1px solid ${brandColors.divider}`,
    borderTop: `1px solid ${brandColors.divider}`,
    transform: "rotate(45deg)",
  };

  const suggestionHeaderSx = {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.5,
    py: 1,
  };

  const suggestionItemSx = {
    mx: 0.5,
    my: 0.25,
    px: 1.25,
    py: 0.85,
    borderRadius: "10px",
    cursor: "pointer",
    transition: "background-color 0.12s ease",
  };

  // Popup genérico para menciones/hashtags: header con título, estado de
  // carga o vacío, y la lista con el item resaltado (mouse u ↑/↓)
  // sincronizado con highlightedIndex para que Enter confirme el mismo que
  // se ve resaltado.
  const renderSuggestionPopup = ({ title, icon, loading, items, emptyMessage, renderItem, onSelect, getKey }) => (
    <Paper elevation={0} sx={suggestionPopupSx}>
      <Box sx={suggestionNubSx} />
      <Box sx={suggestionHeaderSx}>
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.textSecondary }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: brandColors.divider }} />
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 1.5 }}>
          <CircularProgress size={16} thickness={5} />
          <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
            <T>Buscando…</T>
          </Typography>
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" sx={{ color: brandColors.textSecondary, px: 1.5, py: 1.5 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <Box sx={{ py: 0.5 }}>
          {items.map((item, idx) => (
            <Box
              key={getKey(item)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onClick={() => onSelect(item)}
              sx={{
                ...suggestionItemSx,
                backgroundColor: idx === highlightedIndex ? brandColors.inputBackground : "transparent",
              }}
            >
              {renderItem(item)}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );

  return (
    <Box>
      <AppBar ref={appBarRef} position="fixed" sx={{ backgroundColor: barColors.background, color: barColors.text }}>
        <Toolbar disableGutters sx={{ padding: "10px 0" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", maxWidth: { xs: "100%", md: "980px" }, mx: "auto", px: 2, boxSizing: "border-box" }}>
            <BackButton color="inherit" />
            <ForumIcon />
            <Typography variant="h6"><T>Hilos</T></Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Compositor: fijo debajo del AppBar, siempre visible al scrollear el feed.
          transform + backfaceVisibility fuerzan su propia capa de composición:
          sin esto, en Safari/iOS el contenido que scrollea "se cuela" a través
          del bloque fijo durante el scroll táctil aunque tenga background. */}
      <Box
        ref={composeRef}
        sx={{
          position: "fixed",
          top: `${appBarHeight}px`,
          // A la derecha de la sidebar de escritorio, si está (ver App.js).
          left: "var(--sidebar-w, 0px)",
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          backgroundColor: brandColors.backgroundDefault,
          borderBottom: `1px solid ${brandColors.divider}`,
          transform: "translateZ(0)",
          WebkitBackfaceVisibility: "hidden",
          backfaceVisibility: "hidden",
        }}
      >
        <Box sx={{ ...pageContentSx, py: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Avatar src={myProfile?.image} sx={{ width: 40, height: 40 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={8}
                  placeholder="¿Qué está pasando?"
                  value={composeText}
                  onChange={handleComposeTextChange}
                  onBlur={() => {
                    mentionBlurTimeout.current = setTimeout(() => {
                      setMentionQuery(null);
                      setMentionResults([]);
                      setHashtagQuery(null);
                    }, 150);
                  }}
                  onFocus={() => {
                    if (mentionBlurTimeout.current) clearTimeout(mentionBlurTimeout.current);
                  }}
                  onKeyDown={(e) => {
                    const activeList = mentionQuery !== null ? mentionResults : hashtagQuery !== null ? hashtagSuggestions : null;
                    if (activeList && activeList.length > 0) {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setHighlightedIndex((i) => (i + 1) % activeList.length);
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setHighlightedIndex((i) => (i - 1 + activeList.length) % activeList.length);
                        return;
                      }
                      if (e.key === "Enter" || e.key === "Tab") {
                        e.preventDefault();
                        const chosen = activeList[highlightedIndex] || activeList[0];
                        if (mentionQuery !== null) handleSelectMention(chosen);
                        else handleSelectHashtag(chosen);
                        return;
                      }
                    }
                    if (e.key === "Escape" && (mentionQuery !== null || hashtagQuery !== null)) {
                      setMentionQuery(null);
                      setMentionResults([]);
                      setHashtagQuery(null);
                    }
                  }}
                  inputRef={composeInputRef}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ "& .MuiInputBase-root": { px: 1.5, py: 1 } }}
                />

                {mentionQuery !== null && renderSuggestionPopup({
                  title: <T>Cuentas que seguís</T>,
                  icon: <AlternateEmailIcon sx={{ fontSize: 14, color: brandColors.textSecondary }} />,
                  loading: mentionLoading,
                  items: mentionResults,
                  emptyMessage: followingIds.size === 0
                    ? <T>Todavía no seguís a nadie.</T>
                    : <><T>Sin resultados para</T> "{mentionQuery}"</>,
                  getKey: (user) => user.uid,
                  onSelect: handleSelectMention,
                  renderItem: (user) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Avatar src={user.image} sx={{ width: 30, height: 30 }} />
                      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{user.name}</Typography>
                        {user.verified && <VerifiedBadge sx={{ fontSize: "0.85rem" }} />}
                      </Box>
                    </Box>
                  ),
                })}

                {hashtagQuery !== null && hashtagQuery.length >= 2 && renderSuggestionPopup({
                  title: <T>Publicaciones y hashtags</T>,
                  icon: <TagIcon sx={{ fontSize: 14, color: brandColors.textSecondary }} />,
                  loading: linkLoading && hashtagSuggestions.length === 0,
                  items: hashtagSuggestions,
                  emptyMessage: <><T>Sin resultados para</T> "{hashtagQuery}"</>,
                  getKey: (s) => (s.kind === "link" ? `${s.link.type}-${s.link.id}` : `tag-${s.tag}`),
                  onSelect: handleSelectHashtag,
                  renderItem: (s) => (s.kind === "link" ? (
                    <LinkSuggestionRow item={s.link} />
                  ) : (
                    <Typography variant="body2" noWrap sx={{ color: brandColors.accent, fontWeight: 600 }}>
                      #{s.tag}
                    </Typography>
                  )),
                })}
              </Box>

              {(composeImages.length > 0 || composeVideos.length > 0) && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {composeImages.map((url, idx) => (
                    <Box key={idx} sx={{ ...mediaPreviewBoxSx(false), width: 100, height: 100 }}>
                      <img src={url} alt="" style={mediaPreviewStyle} />
                      <IconButton
                        sx={removeMediaButtonSx}
                        onClick={() => setComposeImages((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <CloseIcon sx={removeMediaIconSx} />
                      </IconButton>
                    </Box>
                  ))}
                  {composeVideos.map((url, idx) => (
                    <Box key={idx} sx={{ position: "relative", width: 160 }}>
                      <video src={url} controls style={{ width: "100%", borderRadius: "10px" }} />
                      <IconButton
                        sx={removeMediaButtonSx}
                        onClick={() => setComposeVideos((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <CloseIcon sx={removeMediaIconSx} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IconButton onClick={(e) => setEmojiAnchor(e.currentTarget)} disabled={posting}>
                    <EmojiEmotionsIcon sx={{ color: brandColors.accent }} />
                  </IconButton>

                  <IconButton component="label" disabled={uploading || posting}>
                    <AddPhotoAlternateIcon sx={{ color: brandColors.accent }} />
                    <input hidden type="file" accept="image/*" multiple onChange={handleUploadImages} />
                  </IconButton>

                  {showCameraButtons && (
                    <>
                      <IconButton onClick={() => setCameraMode("photo")} disabled={uploading || posting}>
                        <PhotoCameraIcon sx={{ color: brandColors.accent }} />
                      </IconButton>

                      <IconButton onClick={() => setCameraMode("video")} disabled={uploading || posting}>
                        <VideoLibraryIcon sx={{ color: brandColors.accent }} />
                      </IconButton>
                    </>
                  )}

                  {uploading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                </Box>

                <Button
                  variant="contained"
                  onClick={handlePost}
                  disabled={posting || uploading || (!composeText.trim() && composeImages.length === 0 && composeVideos.length === 0)}
                  sx={{ borderRadius: "999px" }}
                >
                  {posting ? <CircularProgress size={18} color="inherit" /> : <T>Publicar</T>}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, mt: `${appBarHeight + composeHeight}px`, mb: 14 }}>
        <Popover
          open={Boolean(emojiAnchor)}
          anchorEl={emojiAnchor}
          onClose={() => setEmojiAnchor(null)}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", width: 260, p: 1 }}>
            {EMOJIS.map((emoji) => (
              <Box
                key={emoji}
                onClick={() => setComposeText((prev) => prev + emoji)}
                sx={{ fontSize: 22, p: 0.5, cursor: "pointer", "&:hover": { backgroundColor: brandColors.inputBackground, borderRadius: 1 } }}
              >
                {emoji}
              </Box>
            ))}
          </Box>
        </Popover>

        <Popover
          open={Boolean(reactionPickerFor)}
          anchorEl={reactionPickerFor?.anchorEl}
          onClose={() => setReactionPickerFor(null)}
        >
          <Box sx={{ display: "flex", p: 0.5 }}>
            {REACTIONS.map((emoji) => (
              <Box
                key={emoji}
                onClick={() => handleSetReaction(reactionPickerFor.thread, emoji)}
                sx={{
                  fontSize: 24,
                  p: 0.75,
                  cursor: "pointer",
                  borderRadius: "50%",
                  transition: "transform 0.1s ease",
                  "&:hover": { backgroundColor: brandColors.inputBackground, transform: "scale(1.2)" },
                }}
              >
                {emoji}
              </Box>
            ))}
          </Box>
        </Popover>

        <Menu
          anchorEl={optionsMenu?.anchorEl}
          open={Boolean(optionsMenu)}
          onClose={handleCloseOptionsMenu}
        >
          {/* Una republicación no se edita: su contenido es copia del original. */}
          {optionsMenu && optionsMenu.thread.uid === myUid && !optionsMenu.thread.repostOf && (
            <MenuItem
              onClick={() => {
                setEditingThread(optionsMenu.thread);
                handleCloseOptionsMenu();
              }}
            >
              <T>Editar</T>
            </MenuItem>
          )}
          {optionsMenu && optionsMenu.thread.uid === myUid && (
            <MenuItem onClick={() => handleDeleteThread(optionsMenu.thread)}>
              <T>Eliminar</T>
            </MenuItem>
          )}
          {optionsMenu && optionsMenu.author.uid !== myUid && (
            <MenuItem onClick={() => handleReportThread(optionsMenu.thread, optionsMenu.author)}>
              <T>Reportar</T>
            </MenuItem>
          )}
          {optionsMenu && optionsMenu.author.uid !== myUid && (
            <MenuItem onClick={() => handleToggleBlockAuthor(optionsMenu.author)}>
              {blockedIds.includes(optionsMenu.author.uid) ? <T>Desbloquear</T> : <T>Bloquear</T>}
            </MenuItem>
          )}
        </Menu>

        {/* El feed escucha con onSnapshot: el cambio llega solo, sin onSaved. */}
        <EditThreadDialog thread={editingThread} onClose={() => setEditingThread(null)} />

        {activeHashtag && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 1.5 }}>
            <Typography variant="body2" sx={{ color: brandColors.accent, fontWeight: 600 }}>
              #{activeHashtag}
            </Typography>
            <IconButton size="small" onClick={() => setActiveHashtag(null)}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

        {/* Feed */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : visibleThreads.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <ForumIcon sx={{ fontSize: 36, mb: 1, opacity: 0.6 }} />
            <Typography><T>Todavía no hay hilos. ¡Publicá el primero!</T></Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {visibleThreads.map((thread) => {
              const isRepost = !!thread.repostOf;
              const author = isRepost
                ? { uid: thread.originalUid, name: thread.originalName, image: thread.originalImage }
                : { uid: thread.uid, name: thread.name, image: thread.image };
              const content = isRepost
                ? { text: thread.originalText, images: thread.originalImages, videos: thread.originalVideos, mentions: thread.originalMentions, links: thread.originalLinks }
                : { text: thread.text, images: thread.images, videos: thread.videos, mentions: thread.mentions, links: thread.links };
              const liked = (thread.likes || []).includes(myUid);
              const reactions = thread.reactions || {};
              const myReaction = reactions[myUid];
              const reactionCount = Object.keys(reactions).length;
              const originalId = thread.repostOf || thread.id;
              const reposted = myRepostedOriginalIds.has(originalId);

              return (
                <React.Fragment key={thread.id}>
                  <Box sx={{ py: 2 }}>
                    {isRepost && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, color: brandColors.textSecondary }}>
                        <RepeatIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                          {thread.name} <T>republicó</T>
                        </Typography>
                      </Box>
                    )}

                    {renderThreadContent(author, content, thread)}

                    {/* Link a la ficha pública del hilo (ver ThreadDetail) */}
                    <Typography
                      component={Link}
                      to={`/hilos/${thread.repostOf || thread.id}`}
                      variant="caption"
                      sx={{ color: brandColors.textSecondary, display: "block", mb: 1, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                      {formatDate(thread.createdAt)}
                      {!isRepost && thread.editedAt && <> · <T>editado</T></>}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <IconButton size="small" onClick={() => handleToggleLike(thread)}>
                        {liked ? <FavoriteIcon sx={{ fontSize: 20, color: "#e0245e" }} /> : <FavoriteBorderIcon sx={{ fontSize: 20 }} />}
                      </IconButton>
                      <Typography variant="caption">{(thread.likes || []).length || ""}</Typography>

                      <IconButton
                        size="small"
                        onClick={(e) => setReactionPickerFor({ anchorEl: e.currentTarget, thread })}
                      >
                        {myReaction ? (
                          <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{myReaction}</Typography>
                        ) : (
                          <AddReactionOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                      <Typography variant="caption">{reactionCount || ""}</Typography>

                      <IconButton size="small" onClick={() => setCommentsFor(thread.id)}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <Typography variant="caption">{thread.commentsCount || ""}</Typography>

                      <IconButton size="small" onClick={() => handleToggleRepost(thread)}>
                        <RepeatIcon sx={{ fontSize: 20, color: reposted ? brandColors.accent : undefined }} />
                      </IconButton>
                      <Typography variant="caption">{thread.repostsCount || ""}</Typography>

                      {author.uid !== myUid && (
                        <IconButton size="small" onClick={() => handleStartChat(author.uid)} sx={{ ml: "auto" }}>
                          <SendIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ borderColor: brandColors.divider }} />
                </React.Fragment>
              );
            })}
          </List>
        )}
        {/* Si la query llenó el limit puede haber más hilos: el sentinel pide la siguiente tanda. */}
        {!loading && threads.length >= threadsLimit && <Box ref={threadsSentinelRef} sx={{ height: 1 }} />}
      </Container>

      {/* Comentarios */}
      <Dialog open={Boolean(commentsFor)} onClose={() => setCommentsFor(null)} fullWidth maxWidth="sm">
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}><T>Comentarios</T></Typography>
          <List sx={{ maxHeight: 360, overflowY: "auto" }}>
            {comments.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                <T>Sé el primero en comentar.</T>
              </Typography>
            )}
            {comments.map((c) => (
              <Box key={c.id} sx={{ display: "flex", gap: 1.25, mb: 1.5 }}>
                <Avatar component={Link} to={`/profile/${c.uid}`} src={authorPhotos[c.uid] || c.image} sx={{ width: 32, height: 32 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography component={Link} to={`/profile/${c.uid}`} sx={{ fontWeight: 600, fontSize: "0.85rem", color: brandColors.textPrimary, textDecoration: "none" }}>
                    {c.name}
                  </Typography>
                  <Typography variant="body2">{c.text}</Typography>
                </Box>
              </Box>
            ))}
          </List>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Escribí un comentario..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handlePostComment(); }}
            />
            <IconButton onClick={handlePostComment} disabled={!commentText.trim()}>
              <SendIcon sx={{ color: brandColors.accent }} />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Preview de imagen */}
      <Dialog open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} maxWidth="md">
        <DialogContent sx={{ p: 0, backgroundColor: "#000" }}>
          {previewImage && (
            <Box component="img" src={previewImage} alt="" sx={{ width: "100%", display: "block" }} />
          )}
        </DialogContent>
      </Dialog>

      {cameraMode && (
        <CameraCapture
          mode={cameraMode}
          onCapture={handleCameraCapture}
          onClose={() => setCameraMode(null)}
        />
      )}

      <BottomToolbar />
    </Box>
  );
};

export default Hilos;
